import React, { useState } from 'react';
import Layout from './Layout';
import { getCourseById } from '../data/courses';
import { formatPrice, formatInstallments } from '../utils/helpers';
import estrategiaImg from '../images/estrategia-moderna.png';
import './EstrategiaModernaPage.css';

const curriculum = [
  {
    title: 'Os Conceitos Básicos',
    lessons: ['Os Conceitos Básicos da Estratégia'],
  },
  {
    title: 'As Peças Menores',
    lessons: ['Os Bispos', 'O Cavalo', 'Bispo Contra Cavalo', 'O Par de Bispos'],
  },
  {
    title: 'As Torres',
    lessons: ['As Torres — Parte 1', 'As Torres — Parte 2'],
  },
  {
    title: 'A Dama e o Jogo com Peças Maiores',
    lessons: ['A Dama e o Jogo com Peças Maiores'],
  },
  {
    title: 'O Rei',
    lessons: ['O Rei — Parte 1', 'O Rei — Parte 2'],
  },
  {
    title: 'Troca de Material',
    lessons: ['Troca de Material'],
  },
  {
    title: 'Os Peões',
    lessons: [
      'O Peão Passado',
      'O Bloqueio',
      'Tipos Especiais de Peões Passados',
      'O Peão Isolado',
      'O Peão Atrasado',
      'O Par de Peões Isolados',
      'Os Peões Dobrados',
    ],
  },
  {
    title: 'O Centro',
    lessons: [
      'O Centro Clássico',
      'O Pequeno Centro',
      'Tensão no Centro',
      'A Centralização',
      'O Controle das Casas Centrais',
    ],
  },
  {
    title: 'Superioridade nas Alas',
    lessons: [
      'Superioridade nas Alas — Parte 1',
      'Superioridade nas Alas — Parte 2',
      'Superioridade nas Alas — Parte 3',
      'Superioridade nas Alas — Parte 4',
      'Superioridade nas Alas — Parte 5',
    ],
  },
  {
    title: 'O Ataque da Minoria',
    lessons: [
      'O Ataque da Minoria — Parte 1',
      'O Ataque da Minoria — Parte 2',
      'O Ataque da Minoria — Parte 3',
      'O Ataque da Minoria — Parte 4',
    ],
  },
  {
    title: 'Os Pontos Estratégicos',
    lessons: [
      'Os Pontos Estratégicos — Parte 1',
      'Os Pontos Estratégicos — Parte 2',
      'Os Pontos Estratégicos — Parte 3',
    ],
  },
  {
    title: 'Elementos Dinâmicos',
    lessons: [
      'Elementos Dinâmicos — Parte 1',
      'Elementos Dinâmicos — Parte 2',
      'Elementos Dinâmicos — Parte 3',
    ],
  },
  {
    title: 'Métodos para Converter Vantagem',
    lessons: [
      'Métodos para Converter Vantagem — Parte 1',
      'Métodos para Converter Vantagem — Parte 2',
      'Métodos para Converter Vantagem — Parte 3',
    ],
  },
  {
    title: 'Estilo Individual',
    lessons: ['Estilo Individual'],
  },
  {
    title: 'Conformidade e Contradição',
    lessons: ['Conformidade e Contradição'],
  },
];

const totalLessons = curriculum.reduce((sum, s) => sum + s.lessons.length, 0);

function EstrategiaModernaPage({ paymentUrlOverride }) {
  const course = getCourseById('estrategia-moderna');
  const paymentUrl = paymentUrlOverride || course.paymentUrl;
  const [openSection, setOpenSection] = useState(null);

  const toggle = (idx) => {
    setOpenSection(openSection === idx ? null : idx);
  };

  return (
    <Layout title="Estratégia Moderna do Xadrez" favicon="/estrategia.ico">
      {/* Hero */}
      <section className="em-hero">
        <div className="em-hero__inner">
          <img src={estrategiaImg} alt="Estratégia Moderna do Xadrez" className="em-hero__image" />
          <div className="em-hero__content">
            <span className="em-hero__badge">15 seções · {totalLessons} aulas</span>
            <h1 className="em-hero__title">Estratégia Moderna do Xadrez</h1>
            <p className="em-hero__subtitle">
              Baseado no clássico de Luděk Pachman, este curso aprofunda seu entendimento
              estratégico com mais de 40 videoaulas ministradas pelo MN Rafael Leite.
            </p>
            <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="em-hero__cta">
              Quero este curso
            </a>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="em-about">
        <div className="em-about__inner">
          <h2 className="em-about__title">Sobre o Curso</h2>
          <p className="em-about__text">
            "Estratégia Moderna do Xadrez" de Luděk Pachman é uma das obras mais completas
            já escritas sobre o assunto. Neste curso, cada capítulo do livro é transformado
            em videoaulas detalhadas, com exemplos práticos e explicações que vão elevar
            seu nível de jogo.
          </p>
          <div className="em-about__stats">
            <div className="em-about__stat">
              <div className="em-about__stat-value">15</div>
              <div className="em-about__stat-label">Seções</div>
            </div>
            <div className="em-about__stat">
              <div className="em-about__stat-value">{totalLessons}</div>
              <div className="em-about__stat-label">Aulas</div>
            </div>
            <div className="em-about__stat">
              <div className="em-about__stat-value">40+</div>
              <div className="em-about__stat-label">Horas de conteúdo</div>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="em-curriculum">
        <h2 className="em-curriculum__title">Conteúdo Completo do Curso</h2>
        <p className="em-curriculum__subtitle">
          Clique em cada seção para ver as aulas incluídas
        </p>
        <div className="em-curriculum__list">
          {curriculum.map((section, idx) => (
            <div
              key={idx}
              className={`em-curriculum__section ${openSection === idx ? 'em-curriculum__section--open' : ''}`}
            >
              <button className="em-curriculum__header" onClick={() => toggle(idx)}>
                <span className="em-curriculum__number">Seção {String(idx + 1).padStart(2, '0')}</span>
                <span className="em-curriculum__section-title">{section.title}</span>
                <span className="em-curriculum__count">{section.lessons.length} {section.lessons.length === 1 ? 'aula' : 'aulas'}</span>
                <span className="em-curriculum__arrow">{openSection === idx ? '▲' : '▼'}</span>
              </button>
              {openSection === idx && (
                <ul className="em-curriculum__lessons">
                  {section.lessons.map((lesson, lIdx) => (
                    <li key={lIdx} className="em-curriculum__lesson">
                      <span className="em-curriculum__lesson-icon">&#9654;</span>
                      {lesson}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="em-features">
        <h2 className="em-features__title">O que você recebe</h2>
        <div className="em-features__grid">
          <div className="em-features__item">
            <span className="em-features__icon">&#127916;</span>
            <div>
              <div className="em-features__text-title">Videoaulas em HD</div>
              <div className="em-features__text-desc">Conteúdo gravado em alta qualidade com o MN Rafael Leite</div>
            </div>
          </div>
          <div className="em-features__item">
            <span className="em-features__icon">&#9854;</span>
            <div>
              <div className="em-features__text-title">Acesso Vitalício</div>
              <div className="em-features__text-desc">Estude no seu ritmo, sem prazo para expirar</div>
            </div>
          </div>
          <div className="em-features__item">
            <span className="em-features__icon">&#127942;</span>
            <div>
              <div className="em-features__text-title">Certificado de Conclusão</div>
              <div className="em-features__text-desc">Receba certificado ao finalizar o curso</div>
            </div>
          </div>
          <div className="em-features__item">
            <span className="em-features__icon">&#128241;</span>
            <div>
              <div className="em-features__text-title">Acesse de Qualquer Lugar</div>
              <div className="em-features__text-desc">Computador, tablet ou celular — estude onde quiser</div>
            </div>
          </div>
          <div className="em-features__item">
            <span className="em-features__icon">&#128218;</span>
            <div>
              <div className="em-features__text-title">Baseado em Pachman</div>
              <div className="em-features__text-desc">Conteúdo extraído de um dos melhores livros de estratégia já escritos</div>
            </div>
          </div>
          <div className="em-features__item">
            <span className="em-features__icon">&#128176;</span>
            <div>
              <div className="em-features__text-title">Garantia de 7 Dias</div>
              <div className="em-features__text-desc">Se não gostar, devolvemos 100% do seu dinheiro</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="em-final-cta">
        <h2 className="em-final-cta__title">Domine a Estratégia no Xadrez</h2>
        <p className="em-final-cta__subtitle">
          15 seções, {totalLessons} aulas e mais de 40 horas de conteúdo
        </p>
        <div className="em-final-cta__price">{formatPrice(course.price)}</div>
        <p className="em-final-cta__installments">
          ou {formatInstallments(course.price)}
        </p>
        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="em-final-cta__btn">
          Comprar Agora
        </a>
      </section>

      {/* Trust */}
      <section className="em-trust">
        <div className="em-trust__grid">
          <div className="em-trust__item">
            <div className="em-trust__icon-badge">&#128274;</div>
            <div className="em-trust__item-title">Pagamento Seguro</div>
            <div className="em-trust__item-text">Seus dados protegidos com criptografia</div>
          </div>
          <div className="em-trust__item">
            <div className="em-trust__icon-badge">&#9889;</div>
            <div className="em-trust__item-title">Acesso Imediato</div>
            <div className="em-trust__item-text">Comece a estudar logo após o pagamento</div>
          </div>
          <div className="em-trust__item">
            <div className="em-trust__icon-badge">&#128176;</div>
            <div className="em-trust__item-title">7 Dias de Garantia</div>
            <div className="em-trust__item-text">Devolução integral se não ficar satisfeito</div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default EstrategiaModernaPage;
